/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import * as joint from '@joint/plus';
import EventSequence from '../../@types/event-sequence';
import * as canvasActions from './canvas-actions';
import { CanvasController } from './canvas-controller';
import { EsdEvent } from '../../@types/esdEvent';
import { FaultTreeNode } from '../../@types/faultTreeNode';
import TagService from '../../services/tag-service';
import Tag, { TagLinks }  from '../../@types/tag';
import {  LinkedOccurrenceDna, PsfTemplateItemWithCount, EventSequencePsfTemplateItemStats, PsfTemplate, EventSequenceOccurrenceStats } from '../../@types/occurrence-dna';
import EventSequenceService from '../../services/event-sequence.service';
import { toast } from 'react-toastify';
import { setPsfNode } from '../isam-nodes';

export class CanvasEnvironment {
    public controller: CanvasController;
    public paper: joint.dia.Paper;
    public graph: joint.dia.Graph;
    public paperScroller: joint.ui.PaperScroller;
    public selection: joint.dia.Element[] = [];
    public activeTopNode: joint.dia.Element | null = null;
    public eventSequence: EventSequence | null = null;
    public isDirty = false;
    private resizeObserver: ResizeObserver | null = null;
    public allGroupTags: Tag[] = [];
    public esdTagLinks: TagLinks = { eventTags: [], faultTreeNodeTags: [] };
    public isTagsModeActive: boolean = true;
    public isCalloutsModeActive: boolean = false;
    public isOdnaModeActive: boolean = false;
    public readonly: boolean = false;

    public currentLinkedOccurrenceDna: LinkedOccurrenceDna | null = null;
    public psfTemplate: PsfTemplate | null = null;
    public psfCounts: Map<number, Array<PsfTemplateItemWithCount>> = new Map();
    public psfSelectedIds: number[] = [];
    public occurrenceSelectedIds: number[] = [];
    public eventSequenceOccurrenceStats: EventSequenceOccurrenceStats | null = null;

    public dnaPath: joint.dia.Cell[] = [];
    public mask = joint.highlighters.mask;

    public HighlighterFrame = joint.dia.HighlighterView.extend({
        tagName: 'rect',
        attributes: {
            'stroke': '#ed2637',
            'stroke-width': 2,
            'fill': 'none',
            'pointer-events': 'none',
        },
    
        // Method called to highlight a CellView
        highlight(cellView: joint.dia.CellView) {
            const { padding = 0 } = this.options;
        
            const bbox = cellView.model.getBBox();
            // Highlighter is always rendered relatively to the CellView origin
            bbox.x = bbox.y = 0;
            // Increase the size of the Highlighter
            bbox.inflate(padding);
            this.vel.attr(bbox.toJSON());
        },
    });

    public contextMenuCallback: (evt: joint.dia.Event, elementView: joint.dia.ElementView) => void;
    public setSelectedISAMNode: (node: EsdEvent | FaultTreeNode) => void;

    constructor(paperElement: Element,
        contextMenuCallback: (evt: joint.dia.Event, elementView: joint.dia.ElementView) => void,
        setSelectedISAMNode: (node: EsdEvent | FaultTreeNode) => void,
        readonly: boolean) {
        this.contextMenuCallback = contextMenuCallback;
        this.setSelectedISAMNode = setSelectedISAMNode;
        this.readonly = readonly;

        this.graph = new joint.dia.Graph({}, { cellNamespace: canvasActions.allshapes });
        this.paper = new joint.dia.Paper({
            model: this.graph,
            gridSize: 1,
            defaultConnectionPoint: {name: 'boundary', args: {offset: 5}},
            defaultAnchor: { name: 'modelCenter' },
            defaultConnector: { name: 'normal' },
            async: true,
            frozen: true,
            interactive: function(cellView: joint.dia.CellView): any {
                if (cellView.model.get('nodeClass') !== 'CALLOUTNODE' && cellView.model.get('nodeClass') !== 'PSFNODE') return {
                    linkMove: false,
                    labelMove: false,
                    elementMove: false
                };
                return {
                    linkMove: false,
                    labelMove: false,
                    elementMove: true
                };
            
            },
            sorting: joint.dia.Paper.sorting.APPROX,
            cellViewNamespace: {...canvasActions.allshapes},
            background: {
                color: '#F8F9FA',
                image: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAYAAACNMs+9AAAAIElEQVQoU2NkIALcuvU/jZEIdQyjCvGGEjh4QAQxYQkAIfcfBcQyCEYAAAAASUVORK5CYII=',
                repeat: 'repeat',
                size: '20px 20px'
            }
        });
        this.paperScroller = new joint.ui.PaperScroller({
            paper: this.paper,
            padding: { top: 50, left: 480 },
            scrollWhileDragging: true,
            inertia: true,
            autoResizePaper: true,
            borderless: true,
            baseWidth: 1000,
            baseHeight: 1000,
            contentOptions: () => {
                const minArea = new joint.g.Rect(0, 0, 4000, 3000);
                const contentArea = minArea.union(this.graph.getBBox()!);
                return {
                    contentArea
                }
            }
        });
        paperElement.appendChild(this.paperScroller.el);
        this.paperScroller.render();
        this.paperScroller.zoom(1.0, { absolute: true });
        

        this.enableVirtualRendering();

        this.controller = new CanvasController(this);

        // disable the default browser context menu
        window.addEventListener('contextmenu', (event) => {
            event.preventDefault();
        });

    }

    enableVirtualRendering() {
        let { paper, paperScroller: scroller, resizeObserver } = this;
        const viewportOverlap = 50;

        let viewportArea: joint.g.Rect;
        function updateViewportArea() {
            viewportArea = scroller.getVisibleArea().inflate(viewportOverlap);
        }

        updateViewportArea();

        // Watch viewport area
        scroller.el.onscroll = function () {
            updateViewportArea();
        };
        paper.on('scale', updateViewportArea);
        paper.on('blank:pointerdblclick', () => {
            // request full screen
            const element = scroller.el.parentNode as HTMLElement;
            if (element.requestFullscreen) {
                element.requestFullscreen().then(() => {
                    updateViewportArea();
                })
            }
        });


        paper.options.viewport = (view: joint.mvc.View<any>) => {
            const { model } = view;
            if (model.get('hidden')) return false;
            const bbox = model.getBBox();
            if (model.isLink()) {
                // Vertical/horizontal links have zero width/height.
                bbox.inflate(1);
            }
            return viewportArea.intersect(bbox) !== null;
        };

        resizeObserver = new ResizeObserver(() => {
            updateViewportArea();
        });

        resizeObserver.observe(scroller.el.parentNode as Element);

    }

    public destroy(): void {
        this.paper.remove();
        this.paperScroller.remove();
        this.resizeObserver?.disconnect();
    }

    public fetchTagsAndTagLinks(): Promise<void> {
        // fetch tags and tag links
        const groupId = this.eventSequence?.group?.id;
        const esdId = this.eventSequence?.id;
        
        return Promise.all([TagService.getGroupTags(groupId!), TagService.getEventSequenceTags(esdId!)]).then((values) => {
            this.allGroupTags = values[0];
            this.esdTagLinks = values[1];
            return;
        }).catch((error) => {
            throw new Error('Tag returned an error: ' + error);
        });
    }

    public drawDnaPath(): void {
        if (this.currentLinkedOccurrenceDna) {
            let dna = this.currentLinkedOccurrenceDna;
            let dnaPathEventFailure = dna.events.filter((node) => node.failureStatus === 'FAILURE').map((node) => {
                return this.graph.getCell('event-'+ node.id) as joint.dia.Element;
            }).filter((cell) => cell !== undefined);
            let dnaPathEventSuccess = dna.events.filter((node) => node.failureStatus === 'SUCCESS').map((node) => {
                return this.graph.getCell('event-'+ node.id) as joint.dia.Element;
            }).filter((cell) => cell !== undefined);
            let dnaPathFtFailure = dna.faultTreeNodes.filter((node) => node.failureStatus === 'FAILURE').map((node) => {
                return this.graph.getCell('ft-'+ node.id) as joint.dia.Element;
            }).filter((cell) => cell !== undefined);
            let dnaPathFtSuccess = dna.faultTreeNodes.filter((node) => node.failureStatus === 'SUCCESS').map((node) => {
                return this.graph.getCell('ft-'+ node.id) as joint.dia.Element;
            }).filter((cell) => cell !== undefined);


            let dnaPathFailure = dnaPathEventFailure.concat(dnaPathFtFailure);
            let dnaPathSuccess = dnaPathEventSuccess.concat(dnaPathFtSuccess);
            let failedLinks: joint.dia.Cell[] = [];
            dnaPathFailure.forEach((element) => {
                this.graph.getConnectedLinks(element, { outbound: true }).forEach((link) => {
                    let target = link.getTargetElement();
                    if (target) {
                        let found = dnaPathFailure.find((element) => element.id === target.id);
                        if (found) {
                            failedLinks.push(link);
                        }
                    }
                });
            });

            let successLinks: joint.dia.Cell[] = [];
            dnaPathSuccess.forEach((element) => {
                this.graph.getConnectedLinks(element, { outbound: true }).forEach((link) => {
                    let target = link.getTargetElement();
                    if (target) {
                        let found = dnaPathSuccess.find((element) => element.id === target.id);
                        if (found) {
                            successLinks.push(link);
                        }
                    }
                });
            });

            this.unhighlightDnaPath();
            this.highlightDnaPath(failedLinks.concat(dnaPathFailure), 'FAILURE');
            this.highlightDnaPath(successLinks.concat(dnaPathSuccess), 'SUCCESS');
            this.dnaPath = failedLinks.concat(dnaPathFailure).concat(dnaPathSuccess).concat(successLinks);
        }
    }

    private expandDnaPath(dna: LinkedOccurrenceDna): void {
        if (dna) {
            let eventIds = dna.eventIds;
            let event = this.eventSequence?.events.find((event) => eventIds.includes(event.id));
            if (event) {
                let eventElement = this.graph.getCell('event-' + event.id) as joint.dia.Element;
                if (!this.paperScroller.isElementVisible(eventElement)) {
                    this.paperScroller.centerElement(eventElement);
                }
            } else {
                let ftIds = dna.faultTreeNodeIds;
                this.eventSequence?.events.some((event) => {
                    let found = undefined
                    if (event.faultTree && event.faultTree.faultTreeNodes && (found=event.faultTree.faultTreeNodes.find((ftNode: any) => ftIds.includes(ftNode.id)))) {
                        let eventElement = this.graph.getCell('event-' + event.id) as joint.dia.Element;
                        if (eventElement.get('collapsed')) {
                            this.paper.trigger('element:expand', this.paper.findViewByModel(eventElement));
                        }
                        let ftElement = this.graph.getCell('ft-' + found.id) as joint.dia.Element;                        
                        let predecessors = this.graph.getPredecessors(ftElement).reverse();
                        predecessors.forEach((predecessor) => {
                            if (predecessor.get('collapsed')) {
                                this.paper.trigger('element:expand', this.paper.findViewByModel(predecessor));
                            }
                        });
                        if (!this.paperScroller.isElementVisible(ftElement)) {
                            this.paperScroller.centerElement(ftElement);
                        }
                        return true;
                    }
                });
            }
        }
    }

    public setStatusLinkedOccurrenceDna(occurrenceTatus: string): void {
        if (this.currentLinkedOccurrenceDna) {
            this.currentLinkedOccurrenceDna.occurrenceStatus = occurrenceTatus;
            this.saveLinkedOccurrenceDna();
        }
    }

    public setODNAStatusOfCurrentSelectedNode(status: 'FAILURE' | 'SUCCESS' | 'CLEAR'): void {
        if (this.currentLinkedOccurrenceDna && this.selection && this.selection.length > 0) {
            let currentNode = this.selection[0];
            if (currentNode.get('nodeClass') === 'EVENTNODE') {
                let node = this.currentLinkedOccurrenceDna.events.find((node) => node.id === currentNode.get('data').id);
                if (node) {
                    if (status === 'CLEAR') {
                        let idx = this.currentLinkedOccurrenceDna.events.findIndex((node) => node.id === currentNode.get('data').id);
                        this.currentLinkedOccurrenceDna.events.splice(idx, 1);
                        idx = this.currentLinkedOccurrenceDna.eventIds.findIndex((id) => id === currentNode.get('data').id);
                        this.currentLinkedOccurrenceDna.eventIds.splice(idx, 1);
                    } else {
                        node.failureStatus = status;
                    }
                } else {
                    if (status !== 'CLEAR') {
                        this.currentLinkedOccurrenceDna.events.push({ id: currentNode.get('data').id, comment: '', failureStatus: status });
                        this.currentLinkedOccurrenceDna.eventIds.push(currentNode.get('data').id);
                    }
                }
            } else if (currentNode.get('nodeClass') === 'FAULTTREENODE') {
                let node = this.currentLinkedOccurrenceDna.faultTreeNodes.find((node) => node.id === currentNode.get('data').id);
                if (node) {
                    if (status === 'CLEAR') {
                        let idx = this.currentLinkedOccurrenceDna.faultTreeNodes.findIndex((node) => node.id === currentNode.get('data').id);
                        this.currentLinkedOccurrenceDna.faultTreeNodes.splice(idx, 1);
                        idx = this.currentLinkedOccurrenceDna.faultTreeNodeIds.findIndex((id) => id === currentNode.get('data').id);
                        this.currentLinkedOccurrenceDna.faultTreeNodeIds.splice(idx, 1);
                    } else {
                        node.failureStatus = status;
                    }
                } else {
                    if (status !== 'CLEAR') {
                        this.currentLinkedOccurrenceDna.faultTreeNodes.push({ id: currentNode.get('data').id, comment: '', failureStatus: status });
                        this.currentLinkedOccurrenceDna.faultTreeNodeIds.push(currentNode.get('data').id);
                    }
                }
            }
            this.saveLinkedOccurrenceDna();
            this.drawDnaPath();
        }
    }

    public saveLinkedOccurrenceDna(occurrenceId?: number): void {
        if (this.currentLinkedOccurrenceDna) {
            let dna = this.currentLinkedOccurrenceDna;
            if (occurrenceId && occurrenceId !== dna.occurrenceId) {
                // save dna path for a different occurrence
                // this is the case when user select 'Save DNA' from Occurrence Management Actions
                dna.occurrenceId = occurrenceId;
            }
            EventSequenceService.putLinkedOccurrenceDna(dna).then(() => {
                toast.success('DNA path saved successfully');
            }).catch ((error) => {
                toast.error('Failed to save DNA path', error);
            });
        }
    }

    public setLinkedOccurrenceDna(dna: LinkedOccurrenceDna | null): void {
        this.currentLinkedOccurrenceDna = null;  // important to prevent the dna path from being drawn during path expansion
        if (dna) {
            this.expandDnaPath(dna);
            this.currentLinkedOccurrenceDna = dna;
            this.drawDnaPath();   
        } else {
            this.unhighlightDnaPath();
        }
    }

    public clearLinkedOccurrenceDna(): void {
        this.unhighlightDnaPath();
    }
    
    public setDnaPath(dnaPath: joint.dia.Element[]): void {
        if (this.currentLinkedOccurrenceDna === null) return;
        let dna: LinkedOccurrenceDna = {
            occurrenceId: this.currentLinkedOccurrenceDna.occurrenceId,
            events: [],
            faultTreeNodes: [],
            eventSequenceId: this.eventSequence?.id!,
            occurrenceStatus: this.currentLinkedOccurrenceDna.occurrenceStatus,
            eventIds: [],
            faultTreeNodeIds: []
        };
        dna.events = dnaPath.filter((node) => node.get('nodeClass') === 'EVENTNODE').map((node) => {
            return { id: node.get('data').id, comment: '', failureStatus: 'FAILURE' };
        });
        dna.faultTreeNodes = dnaPath.filter((node) => node.get('nodeClass') === 'FAULTTREENODE').map((node) => {
            return { id: node.get('data').id, comment: '', failureStatus: 'FAILURE' };
        });

        if (dnaPath.length > 0) {
            let lastnode = dnaPath[dnaPath.length - 1];
            if (lastnode.get('nodeClass') === 'FAULTTREENODE' && (lastnode.get('data').nodeClass === 'BARRIER') || (lastnode.get('data').nodeClass === 'CIRCUMSTANTIAL')) {
                dna.faultTreeNodes[dna.faultTreeNodes.length - 1].failureStatus = 'SUCCESS';
            } else if (lastnode.get('nodeClass') === 'EVENTNODE') {
                if (lastnode.get('data').type === 'END') {
                    if (lastnode.get('data').outcome === 'POSITIVE') {
                        dna.events[dna.events.length - 1].failureStatus = 'SUCCESS';
                        let nodeParent = this.graph.getNeighbors(lastnode, { inbound: true })[0];
                        let idx = dna.events.findIndex((node) => node.id === nodeParent.get('data').id);
                        dna.events[idx].failureStatus = 'SUCCESS';
                    }
                }
            }

        }
        dna.eventIds = dna.events.map((node) => node.id);
        dna.faultTreeNodeIds = dna.faultTreeNodes.map((node) => node.id);
        this.setLinkedOccurrenceDna(dna);
        this.saveLinkedOccurrenceDna();
    }

    public unhighlightDnaPath(): void {
        if (this.dnaPath.length > 0) {
            this.dnaPath.forEach((element) => {
                let view = element.findView(this.paper);
                if (view) {
                    this.mask.remove(element.findView(this.paper));
                }
            });
            this.dnaPath = [];
        }
    }

    public highlightDnaPath(path: joint.dia.Cell[], mode: 'FAILURE' | 'SUCCESS'): void {
        
        path.forEach((element) => {
            if (element.get('hidden')) return;
            let view = element.findView(this.paper);
            if (!view) return;
            this.mask.add(view, { selector: element.isElement() ? 'root' : 'line' }, 'dna-highlighter', {
                deep: true,
                attrs: {
                    stroke: mode === 'FAILURE' ? '#FF4365' : '#008400',
                    'stroke-width': 8,                    
                    padding: 10
                }
            });
            
        });
    }

    public setPsfCounts({ psfIds, occurrenceIds, psfCounts }: { psfIds: number[], occurrenceIds: number[], psfCounts: EventSequencePsfTemplateItemStats }): void {
        this.graph.getElements().filter((cell) => cell.get('nodeClass') === 'PSFNODE').forEach((psf) => {
            psf.remove();
        });
        
        if (!this.psfTemplate) return;
        this.psfSelectedIds = psfIds;
        this.occurrenceSelectedIds = occurrenceIds;

        if (psfCounts) {
            this.psfCounts.clear();
            psfCounts.faultTreeNodePsfTemplateItemStats.forEach((faultTreeNodePsfTemplateItemStat) => {
                const psfItemWithCounts = faultTreeNodePsfTemplateItemStat.psfTemplateItemStats.map((stat) => {
                    const psfItem = this.psfTemplate!.psfTemplateItems.find(
                        (item) => item.id == stat.psfTemplateItemId
                    );
                    return { ...psfItem, count: stat.numOccurrences } as PsfTemplateItemWithCount;
                });
                this.addPsfTemplateItemCounts(faultTreeNodePsfTemplateItemStat.faultTreeNodeId as number, psfItemWithCounts);

                let element = this.graph.getCell('ft-' + faultTreeNodePsfTemplateItemStat.faultTreeNodeId) as joint.dia.Element;
                if (element) {
                    setPsfNode(this, element, psfItemWithCounts);
                }
            });
        }
    }

    public setEventSequenceOccurrenceStats(stats: EventSequenceOccurrenceStats): void {
        this.eventSequenceOccurrenceStats = stats;
        canvasActions.clearOdnaStats(this);
        canvasActions.addOdnaStatToNodes(this, this.graph.getElements());
    }

    public clearEventSequenceOccurrenceStats(): void {
        this.eventSequenceOccurrenceStats = null;
        canvasActions.clearOdnaStats(this);
    }

    public addPsfTemplateItemCounts(nodeId: number, psfTemplateItemCounts: PsfTemplateItemWithCount[]): void {
        this.psfCounts.set(nodeId, psfTemplateItemCounts);
    }

    public getPsfTemplateItemCounts(nodeId: number): PsfTemplateItemWithCount[] {
        return this.psfCounts.get(nodeId) || [];
    }

    public clearAllPsfTemplateItemCounts(): void {
        this.psfCounts.clear();
    }

    public deletePsfTemplateItemCounts(nodeId: number): void {
        this.psfCounts.delete(nodeId);
    }
}
